import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../utils/api'

export const useTMSImports = (page = 1, perPage = 20, statusFilter = null) => {
  const queryClient = useQueryClient()

  // Get import history
  const {
    data: importHistory,
    isLoading: isLoadingHistory,
    error: historyError,
    refetch: refetchHistory
  } = useQuery({
    queryKey: ['tms-import-history', page, perPage, statusFilter],
    queryFn: async () => {
      const params = {
        page,
        per_page: perPage
      }
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter
      }
      const response = await api.get('/tms/imports', { params })
      return response.data
    },
    refetchInterval: 30000, // Refetch every 30 seconds to get status updates
    retry: 2
  })

  // Get specific import status
  const getImportStatus = (importId) => {
    return useQuery({
      queryKey: ['tms-import-status', importId],
      queryFn: async () => {
        const response = await api.get(`/tms/imports/${importId}/status`)
        return response.data
      },
      enabled: !!importId,
      refetchInterval: (data) => {
        // Poll every 5 seconds if pending or processing, otherwise stop polling
        return data?.status === 'pending' || data?.status === 'processing' ? 5000 : false
      },
      retry: 3
    })
  }

  // Import job to TMS project
  const importJobMutation = useMutation({
    mutationFn: async ({ projectId, jobUuid, importSettings = {} }) => {
      const response = await api.post(`/tms/user/projects/${projectId}/import`, {
        job_uuid: jobUuid,
        ...importSettings
      })
      return response.data
    },
    onSuccess: () => {
      // Refresh import history when a new import is started
      queryClient.invalidateQueries({ queryKey: ['tms-import-history'] })
    }
  })

  const imports = importHistory?.imports || []
  const hasActiveImports = imports.some(imp =>
    imp.status === 'pending' || imp.status === 'processing'
  )

  return {
    // Data
    imports,
    totalCount: importHistory?.total_count || 0,
    totalPages: importHistory?.total_pages || 1,
    currentPage: importHistory?.current_page || page,
    hasMore: importHistory?.has_more || false,
    hasActiveImports,

    // States
    isLoadingHistory,
    historyError,

    // Actions
    refetchHistory,
    getImportStatus,
    importJob: importJobMutation.mutate,

    // Mutation states
    isImporting: importJobMutation.isPending,
    importError: importJobMutation.error
  }
}

export const useTMSImportStatus = (importId) => {
  return useQuery({
    queryKey: ['tms-import-status', importId],
    queryFn: async () => {
      const response = await api.get(`/tms/imports/${importId}/status`)
      return response.data
    },
    enabled: !!importId,
    refetchInterval: (data) => {
      // Poll every 5 seconds if pending or processing, otherwise stop polling
      return data?.status === 'pending' || data?.status === 'processing' ? 5000 : false
    },
    retry: 3
  })
}